import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import ContentLayout from "../../component/ContentLayout";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import axios from "../../util/axios";
import moment from "moment";
import { Context } from "../../component/Context/ErrorContext";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import { Box, Modal } from "@mui/material";

const modalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxWidth: 500,
  width: "100%",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  display: "flex",
  flexDirection: "column" as const,
  gap: 2,
  borderRadius: "10px",
};

const emptyForm = {
  id: 0,
  email: "",
  phone: "",
  otp: "",
  signup_date: "",
};

const SEARCH_DEBOUNCE_MS = 400;

const AbandonedUsers = () => {
  const { setError } = useContext(Context);
  const [abandonedUsers, setAbandonedUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const fetchAbandonedUsers = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    params.append("page", pageNumber.toString());
    params.append("limit", pageSize.toString());
    params.append("sort_by", "signup_date");
    params.append("order", "DESC");
    if (debouncedSearch) params.append("search", debouncedSearch);

    axios({
      URL: `/omw/admin/abandoned_users?${params.toString()}`,
      method: "GET",
    })
      .then((res: any) => {
        if (res?.data?.status) {
          setAbandonedUsers(res?.data?.data?.abandoned_users || []);
          setTotalRecords(res?.data?.data?.total || 0);
        } else {
          setError(res?.data?.message || "Failed to fetch abandoned users");
        }
      })
      .catch((err) =>
        setError(err?.response?.data?.message || "Something went wrong"),
      )
      .finally(() => setLoading(false));
  }, [pageNumber, pageSize, debouncedSearch, setError]);

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPageNumber(1);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchInput]);

  useEffect(() => {
    fetchAbandonedUsers();
  }, [fetchAbandonedUsers]);

  const onPageChange = (action: string, step: number) => {
    const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
    if (action === "prev") {
      setPageNumber(Math.max(1, pageNumber - step));
    } else {
      setPageNumber(Math.min(totalPages, pageNumber + step));
    }
  };

  const applySearch = () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    setDebouncedSearch(searchInput);
    setPageNumber(1);
  };

  const clearFilters = () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    setSearchInput("");
    setDebouncedSearch("");
    setPageNumber(1);
  };

  const openEditModal = (row: any) => {
    setForm({
      id: row.id,
      email: row.email || "",
      phone: row.phone || "",
      otp: row.otp || "",
      signup_date: row.signup_date
        ? moment(row.signup_date).format("YYYY-MM-DDTHH:mm")
        : "",
    });
    setOpenModal(true);
  };

  const closeModal = () => {
    setOpenModal(false);
    setForm(emptyForm);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    if (!form.email.trim()) {
      setError("Email is required");
      return;
    }

    setLoading(true);
    const payload = {
      email: form.email,
      phone: form.phone || undefined,
      otp: form.otp || undefined,
      signup_date: form.signup_date
        ? new Date(form.signup_date).toISOString()
        : undefined,
    };

    axios({
      URL: `/omw/admin/abandoned_users/${form.id}`,
      method: "PATCH",
      data: payload,
    })
      .then(() => {
        closeModal();
        fetchAbandonedUsers();
      })
      .catch((err) => setError(err?.response?.data?.message))
      .finally(() => setLoading(false));
  };

  const deleteAbandonedUser = (id: number) => {
    if (!window.confirm("Are you sure you want to delete this abandoned user?")) {
      return;
    }

    setLoading(true);
    axios({
      URL: `/omw/admin/abandoned_users/${id}`,
      method: "DELETE",
    })
      .then(() => fetchAbandonedUsers())
      .catch((err) => setError(err?.response?.data?.message))
      .finally(() => setLoading(false));
  };

  const [colDefs] = useState<any>([
    {
      field: "email",
      headerName: "Email",
      minWidth: 220,
    },
    {
      field: "phone",
      headerName: "Phone",
      minWidth: 150,
      cellRenderer: (param: any) => param.value || "-",
    },
    {
      field: "otp",
      headerName: "OTP",
      minWidth: 100,
      cellRenderer: (param: any) => param.value || "-",
    },
    {
      field: "signup_date",
      headerName: "Signup Date",
      minWidth: 160,
      cellRenderer: (param: any) => {
        if (!param.value) return "-";
        return (
          <div className="h-full flex flex-col items-center justify-center text-sm">
            <p className="p-0">{moment(param.value).format("MM-DD-YYYY")}</p>
            <p className="p-0 font-bold">
              {moment(param.value).format("hh:mm A")}
            </p>
          </div>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 120,
      maxWidth: 120,
      cellRenderer: (param: any) => (
        <div className="h-full flex items-center justify-center gap-3">
          <EditIcon
            className="text-primary cursor-pointer"
            onClick={() => openEditModal(param.data)}
          />
          <CloseIcon
            className="text-red-600 cursor-pointer"
            onClick={() => deleteAbandonedUser(param.data.id)}
          />
        </div>
      ),
    },
  ]);

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

  return (
    <ContentLayout className="items-start">
      <p className="text-3xl font-bold text-secondary">Abandoned Users</p>

      <div className="mt-4 w-full h-auto bg-white p-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 p-3">
          <input
            className="border border-primary border-solid p-2 text-primary placeholder-primary rounded-md w-full"
            placeholder="Search by email or phone"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applySearch()}
          />
          <div className="flex gap-2">
            <button
              className="bg-primary text-white px-6 py-2 rounded-md hover:bg-opacity-90 disabled:opacity-50"
              onClick={applySearch}
              disabled={loading}
            >
              Search
            </button>
            <button
              className="bg-primary text-white px-6 py-2 rounded-md hover:bg-opacity-90 disabled:opacity-50"
              onClick={clearFilters}
              disabled={loading}
            >
              Clear
            </button>
          </div>
        </div>

        <div className="ag-theme-quartz w-full" style={{ height: 500 }}>
          <AgGridReact
            loading={loading}
            rowData={abandonedUsers}
            columnDefs={colDefs}
            defaultColDef={{
              flex: 1,
              minWidth: 100,
              resizable: true,
            }}
          />
        </div>

        <div className="my-2 px-2 w-full flex justify-end flex-col sm:flex-row gap-4 sm:gap-10">
          <div>
            <label htmlFor="pageSize">Page size:</label>
            <select
              id="pageSize"
              className="w-[100px] border-b border-primary border-solid p-2 outline-none ml-2"
              value={pageSize}
              onChange={(e) => {
                setPageNumber(1);
                setPageSize(+e.target.value);
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
          <div className="flex gap-2 justify-center items-center">
            <KeyboardDoubleArrowLeftIcon
              className="text-lg cursor-pointer"
              onClick={() => (pageNumber < 2 ? null : onPageChange("prev", 2))}
            />
            <KeyboardArrowLeftIcon
              className="text-lg cursor-pointer"
              onClick={() => (pageNumber < 2 ? null : onPageChange("prev", 1))}
            />
            <p>
              Page {pageNumber} of {totalPages}
            </p>
            <KeyboardArrowRightIcon
              className="text-lg cursor-pointer"
              onClick={() =>
                pageNumber >= totalPages ? null : onPageChange("next", 1)
              }
            />
            <KeyboardDoubleArrowRightIcon
              className="text-lg cursor-pointer"
              onClick={() =>
                pageNumber >= totalPages ? null : onPageChange("next", 2)
              }
            />
          </div>
        </div>
        <div className="px-3 pb-3 text-sm text-gray-500">
          Total Records: {totalRecords}
        </div>
      </div>

      <Modal open={openModal} onClose={closeModal}>
        <Box sx={modalStyle}>
          <p className="text-2xl font-bold">Update Abandoned User</p>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <label htmlFor="email" className="text-sm font-semibold">
                Email *
              </label>
              <input
                id="email"
                name="email"
                type="email"
                className="bg-white w-full rounded-md p-2 border border-gray outline-none"
                value={form.email}
                onChange={handleFormChange}
                required
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="phone" className="text-sm font-semibold">
                Phone
              </label>
              <input
                id="phone"
                name="phone"
                className="bg-white w-full rounded-md p-2 border border-gray outline-none"
                value={form.phone}
                onChange={handleFormChange}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="otp" className="text-sm font-semibold">
                OTP
              </label>
              <input
                id="otp"
                name="otp"
                className="bg-white w-full rounded-md p-2 border border-gray outline-none"
                value={form.otp}
                onChange={handleFormChange}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="signup_date" className="text-sm font-semibold">
                Signup Date
              </label>
              <input
                id="signup_date"
                name="signup_date"
                type="datetime-local"
                className="bg-white w-full rounded-md p-2 border border-gray outline-none"
                value={form.signup_date}
                onChange={handleFormChange}
              />
            </div>
          </div>
          <div className="flex gap-3 justify-end mt-2">
            <button
              className="px-6 py-2 rounded-md border border-gray"
              onClick={closeModal}
            >
              Cancel
            </button>
            <button
              className="bg-primary text-white px-6 py-2 rounded-md disabled:opacity-50"
              onClick={handleSubmit}
              disabled={loading}
            >
              Update
            </button>
          </div>
        </Box>
      </Modal>
    </ContentLayout>
  );
};

export default AbandonedUsers;
